import { BaseQueryFn, createApi, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

interface IGetVideoQueries {
  queries: Object;
  id: number;
}

interface IGetMoviesList {
  queries: Object;
  mainFilter: string;
  noCache: boolean;
}

interface IGetPersonsList {
  queries: Object;
}

interface IGetPersonInfo {
  queries: Object;
  id: number | string | undefined;
}

interface IGetVideoInfo {
  queries: Object;
  id: number;
}

const baseQueryWithReauth: BaseQueryFn<any, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const { noCache, ...restArgs } = args; // 👈 вытаскиваем кастомное поле

  const baseQueryWithCustomHeaders = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_MAIN_URL,
    prepareHeaders: (headers) => {
      if (noCache) {
        headers.set("Cache-Control", "no-cache");
        // headers.set("Pragma", "no-cache");
      }
      return headers;
    },
  });

  let result = await baseQueryWithCustomHeaders(restArgs, api, extraOptions);

  // если 401 → рефреш логика
  // ...

  return result;
};

const generateQueryStr = (baseString: string, query: Object): string => {
  return (
    baseString +
    Object.entries(query)
      .map(([key, value]) => `${key}=${value}`)
      .join("&")
  );
};

export const filmsAPI = createApi({
  reducerPath: "films",
  baseQuery: baseQueryWithReauth,
  refetchOnFocus: false,
  endpoints: (builder) => ({
    getFilmsList: builder.query<any, Object>({
      query: (queryParams: IGetMoviesList) => {
        const queryStr = generateQueryStr(`${queryParams.mainFilter}?`, queryParams.queries);
        return { url: queryStr };
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, otherArgs) => {
        if (newItems.page === 1) {
          currentCache.results = newItems.results;
          return;
        }

        currentCache.results.push(...newItems.results);
        currentCache.results.filter(
          (value: any, index: any, self: any) => index === self.findIndex((t: any) => t.id === value.id),
        );
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getSearchFilmsList: builder.query<any, Object>({
      query: (queryParams: IGetMoviesList) => {
        const queryStr = generateQueryStr(`${queryParams.mainFilter}?`, queryParams.queries);
        const accessToken = localStorage.getItem("accessToken");

        return {
          url: queryStr,
          headers: {
            accept: "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          noCache: queryParams.noCache,
        };
      },
    }),
    getAISearchFilmsList: builder.query<any, { q: string; language: string; limit?: number; page?: number }>({
      query: ({ q, language, limit = 20, page = 1 }) => {
        const accessToken = localStorage.getItem("accessToken");
        const queryStr = generateQueryStr("/movies/search?", { q, language, limit, page });

        return {
          url: queryStr,
          headers: {
            accept: "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        };
      },
    }),
    getPersonsList: builder.query<any, IGetPersonsList>({
      query: (queryParams) => {
        const accessToken = localStorage.getItem("accessToken");
        const queryStr = generateQueryStr("/person/?", queryParams.queries);

        return {
          url: queryStr,
          headers: {
            accept: "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        };
      },
    }),
    getPersonDetails: builder.query<any, IGetPersonInfo>({
      query: (queryParams) => {
        const accessToken = localStorage.getItem("accessToken");
        const queryStr = generateQueryStr(`/person/${queryParams.id}?`, queryParams.queries);

        return {
          url: queryStr,
          headers: {
            accept: "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        };
      },
    }),
    getPersonMovies: builder.query<any, IGetPersonInfo>({
      query: (queryParams) => {
        const accessToken = localStorage.getItem("accessToken");
        const queryStr = generateQueryStr(`/person/${queryParams.id}/movies?`, queryParams.queries);

        return {
          url: queryStr,
          headers: {
            accept: "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        };
      },
    }),
    getPersonRelated: builder.query<any, IGetPersonInfo>({
      query: (queryParams) => {
        const accessToken = localStorage.getItem("accessToken");
        const queryStr = generateQueryStr(`/person/${queryParams.id}/related?`, queryParams.queries);

        return {
          url: queryStr,
          headers: {
            accept: "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        };
      },
    }),
    getFavouritesFilmsList: builder.query<any, Object>({
      query: (queryParams: IGetMoviesList) => {
        const queryStr = generateQueryStr(`${queryParams.mainFilter}?`, queryParams.queries);
        const accessToken = localStorage.getItem("accessToken");
        return {
          url: queryStr,
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        };
      },
      keepUnusedDataFor: 0,
    }),
    getFavouritePersonsList: builder.query<any, IGetPersonsList>({
      query: (queryParams) => {
        const accessToken = localStorage.getItem("accessToken");
        const queryStr = generateQueryStr("/person/favorites/?", queryParams.queries);

        return {
          url: queryStr,
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        };
      },
      keepUnusedDataFor: 0,
    }),
    getCurrentVideo: builder.query<any, Object>({
      query: (queryParams: IGetVideoQueries) => {
        const queryStr = generateQueryStr(`/movies/${queryParams.id}/videos?`, queryParams.queries);
        return { url: queryStr };
      },
    }),
    getVideoDetails: builder.query<any, Object>({
      query: (queryParams: IGetVideoInfo) => {
        const queryStr = generateQueryStr(`/movies/${queryParams.id}?`, queryParams.queries);
        return { url: queryStr };
      },
    }),
    getCastsList: builder.query<any, Object>({
      query: (queryParams: IGetVideoInfo) => {
        const queryStr = generateQueryStr(`/movies/${queryParams.id}/credits?`, queryParams.queries);
        return { url: queryStr };
      },
    }),
    getReviewsList: builder.query<any, Object>({
      query: (queryParams: IGetVideoInfo) => {
        const queryStr = generateQueryStr(`/movies/${queryParams.id}/reviews?`, queryParams.queries);
        const accessToken = localStorage.getItem("accessToken");

        return {
          url: queryStr,
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        };
      },
    }),
    getMovieImages: builder.query<any, Object>({
      query: (queryParams: IGetVideoInfo) => {
        const queryStr = generateQueryStr(`/movies/${queryParams.id}/images?`, queryParams.queries);
        return { url: queryStr };
      },
    }),
    getIdsList: builder.query<any, Object>({
      query: (queryParams: IGetMoviesList) => {
        const queryStr = generateQueryStr(`${queryParams.mainFilter}`, {});
        const accessToken = localStorage.getItem("accessToken");
        return {
          url: queryStr,
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        };
      },
    }),
    getPersonFavouriteIdsList: builder.query<any, void>({
      query: () => {
        const accessToken = localStorage.getItem("accessToken");

        return {
          url: "/person/favorites/ids/",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        };
      },
    }),
  }),
});

export const {
  useGetFilmsListQuery,
  useGetSearchFilmsListQuery,
  useGetAISearchFilmsListQuery,
  useGetPersonsListQuery,
  useGetPersonDetailsQuery,
  useGetPersonMoviesQuery,
  useGetPersonRelatedQuery,
  useGetCurrentVideoQuery,
  useGetCastsListQuery,
  useGetReviewsListQuery,
  useGetVideoDetailsQuery,
  useGetFavouritesFilmsListQuery,
  useGetFavouritePersonsListQuery,
  useGetMovieImagesQuery,
  useGetIdsListQuery,
  useGetPersonFavouriteIdsListQuery,
} = filmsAPI;
